// White-label / Agency mode (audit-handoff Phase 3 §B.1).
//
// On app boot we hit the unauthenticated `/api/public/branding` lookup with
// the current `window.location.host` so the login screen can render under
// the agency's branding *before* the user authenticates.
//
// The resolved palette + logo are exposed through `BrandingContext` and
// applied as CSS variables on `<html>` so any Tailwind component using
// `var(--brand-primary)` etc. picks them up automatically.

import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';

const NEUTRAL_BRANDING = Object.freeze({
  logo_url: null,
  primary_color: null,
  secondary_color: null,
  login_subtitle: null,
  hide_xpnsr_branding: false,
});

const BrandingContext = React.createContext({
  branding: NEUTRAL_BRANDING,
  loading: true,
  refresh: () => {},
});

function applyCssVariables(branding) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  if (branding.primary_color) {
    root.style.setProperty('--brand-primary', branding.primary_color);
  } else {
    root.style.removeProperty('--brand-primary');
  }
  if (branding.secondary_color) {
    root.style.setProperty('--brand-secondary', branding.secondary_color);
  } else {
    root.style.removeProperty('--brand-secondary');
  }
}

export function BrandingProvider({ apiBase, children }) {
  const [branding, setBranding] = useState(NEUTRAL_BRANDING);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (typeof window === 'undefined') return;
    if (!apiBase) {
      setLoading(false);
      return;
    }
    try {
      const host = window.location.host || '';
      const resp = await axios.get(`${apiBase}/public/branding`, { params: { host } });
      const next = { ...NEUTRAL_BRANDING, ...(resp?.data || {}) };
      setBranding(next);
      applyCssVariables(next);
    } catch (_err) {
      // Network or transport-level failure — fall back to the neutral
      // palette. A 4xx still produces a JSON body; only network errors
      // hit this branch.
      setBranding(NEUTRAL_BRANDING);
      applyCssVariables(NEUTRAL_BRANDING);
    } finally {
      setLoading(false);
    }
  }, [apiBase]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const value = useMemo(() => ({ branding, loading, refresh }), [branding, loading, refresh]);

  return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>;
}

export function useBranding() {
  return useContext(BrandingContext);
}

// Exported for the unit tests so they can assert on the neutral payload
// without importing the symbol from a deeper path.
export { NEUTRAL_BRANDING, applyCssVariables };
